// The layout bundle and page modules can both import this file. Keep their form
// hooks, pending writes and hydration state together, regardless of module URL.
const stateKey = Symbol.for('unionstack.json-form.state');
const state = globalThis[stateKey] ??= {
    beforeSubmitHandlers: new WeakMap(),
    resetTimers: new WeakMap(),
    activeSubmissions: new WeakSet(),
    originalControlValues: new WeakMap(),
};
const { beforeSubmitHandlers, resetTimers, activeSubmissions, originalControlValues } = state;

function resolveUrl(path) {
    if (!path || !path.startsWith('/')) {
        return path;
    }

    const prefix = document.documentElement.dataset.urlPrefix;
    if (!prefix || prefix === '/') {
        return path;
    }

    return `${prefix.endsWith('/') ? prefix.slice(0, -1) : prefix}${path}`;
}

function normalizeValue(control, value) {
    const original = originalControlValues.get(control);
    if (original && !control.multiple && original.displayed === value) return original.value;
    if (control.dataset.usValueType === 'boolean') {
        return value === 'true';
    }
    if (control instanceof HTMLInputElement && ['date', 'time', 'datetime-local'].includes(control.type) && String(value).trim() === '') {
        return null;
    }
    if (control instanceof HTMLInputElement && control.type === 'datetime-local' && !control.hasAttribute('data-us-wall-time') && String(value).trim() !== '') {
        return window.UnionStackTime ? window.UnionStackTime.inputUtc(control, String(value)) : new Date(String(value)).toISOString();
    }

    const nullValues = control.dataset.usNullValues?.split('|') ?? [];
    if (nullValues.includes(value)) {
        return null;
    }

    if (control.dataset.usValueType === 'number'
        || control instanceof HTMLInputElement && control.type === 'number') {
        if (String(value).trim() === '') {
            return null;
        }

        const number = Number(value);
        return Number.isFinite(number) ? number : value;
    }

    if (control instanceof HTMLInputElement
        && control.type === 'tel'
        && (value === '' || value === 'null' || value === '(___) ___-____')) {
        return null;
    }

    return value;
}

function assignValue(target, name, value, forceArray = false) {
    const segments = name
        .replace(/\[([^\]]*)\]/g, '.$1')
        .split('.')
        .filter(Boolean);

    if (segments.length === 0) {
        return;
    }

    if (segments.some(segment => ['__proto__', 'constructor', 'prototype'].includes(segment))) {
        throw globalThis.UnionStackI18n.error("Common.ClientTheFormContainsAnUnsupportedFieldName4974dc");
    }

    let destination = target;
    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];
        if (!Object.hasOwn(destination, segment)) {
            destination[segment] = /^\d+$/.test(segments[index + 1]) ? [] : {};
        }
        destination = destination[segment];
    }

    const finalSegment = segments.at(-1);
    if (!Object.hasOwn(destination, finalSegment)) {
        destination[finalSegment] = forceArray ? [value] : value;
        return;
    }

    if (!Array.isArray(destination[finalSegment])) {
        destination[finalSegment] = [destination[finalSegment]];
    }

    destination[finalSegment].push(value);
}

function serializeJsonForm(form) {
    const result = {};
    const namedControls = Array.from(form.elements).filter(control => control.name);

    for (const control of form.elements) {
        if (!(control instanceof HTMLInputElement
            || control instanceof HTMLSelectElement
            || control instanceof HTMLTextAreaElement)
            || !control.name
            || control.matches(':disabled')) {
            continue;
        }

        if (control instanceof HTMLInputElement && control.type === 'file') {
            continue;
        }

        if (control instanceof HTMLInputElement
            && control.type === 'hidden'
            && control.value === 'false'
            && namedControls.some(candidate => candidate instanceof HTMLInputElement
                && candidate.type === 'checkbox'
                && candidate.name === control.name)) {
            continue;
        }

        if (control instanceof HTMLInputElement && control.type === 'radio') {
            if (control.checked) {
                assignValue(result, control.name, normalizeValue(control, control.value));
            }
            continue;
        }

        if (control instanceof HTMLInputElement && control.type === 'checkbox') {
            const isBoolean = control.value === 'true' || control.value === 'on';
            if (isBoolean) {
                assignValue(result, control.name, control.checked);
            } else if (control.checked) {
                const isGroup = namedControls.filter(candidate => candidate instanceof HTMLInputElement
                    && candidate.type === 'checkbox'
                    && candidate.name === control.name).length > 1;
                assignValue(result, control.name, normalizeValue(control, control.value), isGroup);
            }
            continue;
        }

        if (control instanceof HTMLSelectElement && control.multiple) {
            assignValue(
                result,
                control.name,
                Array.from(control.selectedOptions, option => normalizeValue(control, option.value)),
            );
            continue;
        }

        assignValue(result, control.name, normalizeValue(control, control.value));
    }

    const root = form.dataset.usJsonRoot;
    return root && result[root] && typeof result[root] === 'object' ? result[root] : result;
}

async function responseError(response) {
    let problem = null;
    try {
        problem = await response.json();
    } catch {
        // The selected-language status mapping also handles non-JSON responses.
    }
    const error = globalThis.UnionStackI18n.problemError(problem, response.status);
    error.validationErrors = error.fields;
    return error;
}

async function requestJsonResponse(url, options = {}) {
    const headers = new Headers(options.headers);
    headers.set('Accept', 'application/json');

    if (options.body !== undefined && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfMeta?.content;
    if (csrfToken) {
        headers.set(csrfMeta.dataset.csrfHeader || 'RequestVerificationToken', csrfToken);
    }

    const response = await fetch(resolveUrl(url), {
        ...options,
        credentials: 'same-origin',
        headers,
    });

    if (!response.ok) {
        throw await responseError(response);
    }

    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
        return { response, result: null };
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    const result = contentType.includes('json') ? await response.json() : await response.text();
    return { response, result };
}

async function requestJson(url, options = {}) {
    return (await requestJsonResponse(url, options)).result;
}

function unquoteEtag(value) {
    if (!value) {
        return null;
    }

    const normalized = value.trim().replace(/^W\//i, '');
    return normalized.startsWith('"') && normalized.endsWith('"')
        ? normalized.slice(1, -1)
        : normalized;
}

function propertyValue(source, name) {
    if (!source || typeof source !== 'object' || !name) {
        return undefined;
    }

    const segments = name.replace(/\[([^\]]*)\]/g, '.$1').split('.').filter(Boolean);
    let value = source;
    for (const segment of segments) {
        if (!value || typeof value !== 'object') {
            return undefined;
        }

        const key = Object.keys(value).find(candidate =>
            candidate.localeCompare(segment, undefined, { sensitivity: 'accent' }) === 0);
        if (!key) {
            return undefined;
        }
        value = value[key];
    }
    return value;
}

function setControlValue(control, value) {
    if (control instanceof HTMLInputElement && control.type === 'file') {
        return;
    }

    if (control instanceof HTMLInputElement && control.type === 'checkbox') {
        control.checked = Boolean(value);
        return;
    }

    if (control instanceof HTMLInputElement && control.type === 'radio') {
        control.checked = String(control.value) === String(value ?? '');
        return;
    }

    if (control instanceof HTMLSelectElement && control.multiple) {
        const selected = new Set((Array.isArray(value) ? value : []).map(String));
        for (const option of control.options) {
            option.selected = selected.has(option.value);
        }
        return;
    }

    if (control instanceof HTMLInputElement && control.type === 'date') {
        const serialized = value === null || value === undefined ? '' : String(value);
        const dateOnly = serialized.match(/^(\d{4}-\d{2}-\d{2})(?:T|\s|$)/);
        control.value = dateOnly?.[1] ?? serialized;
        return;
    }

    if (control instanceof HTMLInputElement && control.type === 'datetime-local'
        && !control.hasAttribute('data-us-wall-time')) {
        if (value) {
            control.dataset.usInstantValue = String(value);
            control.value = window.UnionStackTime ? window.UnionStackTime.inputValue(value) : String(value).replace(/Z$/, '');
        } else {
            delete control.dataset.usInstantValue;
            control.value = '';
        }
        return;
    }

    control.value = value === null || value === undefined ? '' : String(value);
}

function hydrateJsonForm(form, representation) {
    if (!representation || typeof representation !== 'object') {
        return;
    }

    for (const control of form.elements) {
        if (!(control instanceof HTMLInputElement
            || control instanceof HTMLSelectElement
            || control instanceof HTMLTextAreaElement)
            || !control.name) {
            continue;
        }

        const root = form.dataset.usJsonRoot;
        const name = root && control.name.startsWith(`${root}.`)
            ? control.name.slice(root.length + 1)
            : control.name;
        const value = propertyValue(representation, name);
        if (value !== undefined) {
            setControlValue(control, value);
            control.dispatchEvent(new Event('change', { bubbles: true }));
            originalControlValues.set(control, { value, displayed: control.value });
        }
    }
}

function updateVersionControls(form, representation, response) {
    const version = representation?.rowVersion
        ?? representation?.version
        ?? unquoteEtag(response?.headers?.get('ETag'));
    if (version === undefined || version === null) {
        return;
    }

    for (const control of form.querySelectorAll('input[name]')) {
        const name = normalizedFieldName(control.name);
        if (name === 'rowversion' || name === 'version' || name === 'expectedversion') {
            control.value = String(version);
        }
    }
}

async function refreshJsonForm(form, url, response) {
    if (!(form instanceof HTMLFormElement) || !url) {
        return null;
    }

    const result = await requestJson(url, { method: 'GET' });
    hydrateJsonForm(form, result);
    updateVersionControls(form, result, response);
    form.dispatchEvent(new CustomEvent('unionstack:form:refreshed', { detail: { result } }));
    return result;
}

function interpolateUrl(template, result) {
    return template.replace(/\{([^}]+)\}/g, (match, property) => {
        const value = propertyValue(result, property);
        return value === undefined || value === null ? match : encodeURIComponent(String(value));
    });
}

function canonicalRefreshUrl(form, response, result) {
    const configured = form.dataset.usRefreshUrl;
    if (configured) {
        return interpolateUrl(configured, result);
    }

    const location = response.headers.get('Location');
    if (location) {
        return location;
    }

    const method = (form.getAttribute('method') || 'POST').toUpperCase();
    return method === 'PUT' || method === 'PATCH'
        ? form.getAttribute('action')
        : null;
}

function concurrencyHeaders(form) {
    const fieldName = form.dataset.usConcurrencyField;
    const control = fieldName
        ? form.elements.namedItem(fieldName)
        : form.querySelector(
            'input[name="expectedVersion"], input[name="rowVersion"], input[name="version"], [data-us-concurrency-version]',
        );
    const value = control instanceof HTMLInputElement
        || control instanceof HTMLSelectElement
        || control instanceof HTMLTextAreaElement
        ? control.value.trim()
        : '';
    if (!value) {
        return undefined;
    }

    return { 'If-Match': `"${value.replace(/^W\//i, '').replace(/^"|"$/g, '')}"` };
}

function setBusy(form, busy) {
    const surface = form.closest('.card') ?? form;
    surface.dataset.usBusy = String(busy);
    surface.setAttribute('aria-busy', String(busy));

    for (const button of form.querySelectorAll('button[type="submit"], input[type="submit"]')) {
        button.disabled = busy;
    }
}

function normalizedFieldName(name) {
    return String(name || '').split('.').at(-1).toLowerCase();
}

function ensureFormFeedback(form) {
    const existing = form.querySelector('[data-us-form-feedback]');
    if (existing instanceof HTMLElement) {
        return existing;
    }

    const feedback = document.createElement('div');
    feedback.className = 'text-danger mt-3 validation-summary-valid';
    feedback.dataset.usFormFeedback = '';
    feedback.setAttribute('role', 'alert');
    feedback.setAttribute('aria-live', 'assertive');
    feedback.hidden = true;

    const actions = form.querySelector('[data-us-form-actions], .modal-footer');
    if (actions?.parentElement) {
        actions.before(feedback);
    } else {
        form.append(feedback);
    }

    return feedback;
}

function clearServerValidation(form) {
    const feedback = ensureFormFeedback(form);
    feedback.textContent = '';
    feedback.hidden = true;
    feedback.classList.remove('validation-summary-errors');
    feedback.classList.add('validation-summary-valid');

    for (const control of form.querySelectorAll('[data-us-server-invalid]')) {
        control.classList.remove('input-validation-error');
        control.removeAttribute('aria-invalid');
        delete control.dataset.usServerInvalid;
    }

    for (const message of form.querySelectorAll('[data-us-server-validation]')) {
        message.textContent = '';
        message.classList.remove('field-validation-error');
        message.classList.add('field-validation-valid');
        delete message.dataset.usServerValidation;
    }
}

function applyServerValidation(form, errors) {
    if (!errors || typeof errors !== 'object') {
        return;
    }

    const controls = Array.from(form.elements).filter(control => control.name);
    const validationMessages = Array.from(form.querySelectorAll('[data-valmsg-for]'));
    const fieldMatches = (name, key) => {
        const path = value => {
            let result = String(value || '').replace(/\[([^\]]+)\]/g, '.$1').toLowerCase().replace(/^request\./, '');
            const root = form.dataset.usJsonRoot?.toLowerCase();
            if (root && result.startsWith(`${root}.`)) result = result.slice(root.length + 1);
            return result;
        };
        const candidate = path(name);
        const expected = path(key);
        return candidate === expected || expected.endsWith(`.${candidate}`) || candidate.endsWith(`.${expected}`);
    };

    for (const [key, values] of Object.entries(errors)) {
        if (!key) {
            continue;
        }

        const messages = (Array.isArray(values) ? values : [values]).filter(Boolean);
        if (messages.length === 0) {
            continue;
        }

        for (const control of controls.filter(item => fieldMatches(item.name, key))) {
            control.classList.add('input-validation-error');
            control.setAttribute('aria-invalid', 'true');
            control.dataset.usServerInvalid = 'true';
        }

        const message = validationMessages.find(item =>
            fieldMatches(item.dataset.valmsgFor, key));
        if (message) {
            message.textContent = messages.join(' ');
            message.classList.remove('field-validation-valid');
            message.classList.add('field-validation-error');
            message.dataset.usServerValidation = 'true';
        }
    }
}

function setFormState(form, state, label, feedbackMessage = label) {
    const feedback = ensureFormFeedback(form);
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    const submitLabel = submitButton?.querySelector('[data-us-submit-label]');
    const initialLabel = submitLabel?.dataset.usInitialLabel ?? submitLabel?.textContent?.trim();

    if (submitLabel && initialLabel) {
        submitLabel.dataset.usInitialLabel = initialLabel;
        submitLabel.textContent = label;
    }

    if (submitButton) {
        submitButton.classList.toggle('btn-primary', state === 'idle');
        submitButton.classList.toggle('btn-success', state === 'success');
        submitButton.classList.toggle('btn-danger', state === 'error');
    }

    if (feedback) {
        const hasError = state === 'error' && Boolean(feedbackMessage);
        feedback.textContent = hasError ? feedbackMessage : '';
        feedback.hidden = !hasError;
        feedback.classList.toggle('validation-summary-valid', !hasError);
        feedback.classList.toggle('validation-summary-errors', hasError);
    }

    window.UnionStack?.ui?.announce(feedbackMessage, state === 'error' ? 'assertive' : 'polite');

    const existingTimer = resetTimers.get(form);
    if (existingTimer) {
        window.clearTimeout(existingTimer);
    }

    if (state !== 'idle') {
        resetTimers.set(form, window.setTimeout(() => {
            if (submitLabel && initialLabel) {
                submitLabel.textContent = initialLabel;
            }
            submitButton?.classList.remove('btn-success', 'btn-danger');
            submitButton?.classList.add('btn-primary');
            resetTimers.delete(form);
        }, 3000));
    }
}

function registerBeforeSubmit(form, handler) {
    const handlers = beforeSubmitHandlers.get(form) ?? [];
    handlers.push(handler);
    beforeSubmitHandlers.set(form, handlers);
}

async function submitForm(form) {
    if (activeSubmissions.has(form)) {
        return;
    }

    form.classList.add('was-validated');
    // Native required text controls accept spaces, while the form asks for a value.
    // Keep meaningful whitespace and credentials intact; only empty required text is normalized.
    for (const control of form.querySelectorAll('input[required], textarea[required]')) {
        if (!control.matches(':disabled') && !control.readOnly
            && (control.tagName === 'TEXTAREA' || ['text', 'search', 'email', 'tel', 'url'].includes(control.type))
            && !control.value.trim()) {
            control.value = '';
        }
    }
    if (!form.checkValidity()) {
        window.UnionStack?.ui?.focusFirstInvalid(form);
        return;
    }

    activeSubmissions.add(form);
    clearServerValidation(form);
    setBusy(form, true);
    try {
        const payload = form.dataset.usFormData === 'true'
            ? new FormData(form)
            : serializeJsonForm(form);
        for (const handler of beforeSubmitHandlers.get(form) ?? []) {
            await handler({ form, payload });
        }

        const mutation = await requestJsonResponse(form.getAttribute('action'), {
            method: form.getAttribute('method') || 'POST',
            headers: concurrencyHeaders(form),
            body: payload instanceof FormData ? payload : JSON.stringify(payload),
        });

        let result = mutation.result;
        let refreshError = null;
        const refreshUrl = canonicalRefreshUrl(form, mutation.response, mutation.result);
        if (refreshUrl) {
            try {
                result = await requestJson(refreshUrl, { method: 'GET' });
            } catch (error) {
                refreshError = error;
            }
        }

        hydrateJsonForm(form, result);
        updateVersionControls(form, result ?? mutation.result, mutation.response);

        form.classList.remove('was-validated');
        setFormState(form, 'success', form.dataset.usSuccessLabel || globalThis.UnionStackI18n.text('Common.Success'));
        form.dispatchEvent(new CustomEvent('unionstack:form:success', {
            detail: {
                result,
                mutationResult: mutation.result,
                refreshError,
            },
        }));
        const redirectTemplate = form.dataset.usSuccessRedirect;
        if (redirectTemplate) {
            window.location.assign(resolveUrl(interpolateUrl(redirectTemplate, result ?? mutation.result)));
            return;
        }
        if (form.dataset.usSuccessReload === 'true') {
            window.location.reload();
            return;
        }
        if (refreshError) {
            window.UnionStack?.ui?.announce(
                globalThis.UnionStackI18n.text('Common.MessageSavedRefreshFailed'),
                'assertive',
            );
        }
    } catch (error) {
        const message = globalThis.UnionStackI18n.localMessage(error) || globalThis.UnionStackI18n.text('Common.MessageRequestFailed');
        applyServerValidation(form, error?.validationErrors);
        setFormState(form, 'error', form.dataset.usErrorLabel || globalThis.UnionStackI18n.text('Common.Error'), message);
        window.UnionStack?.ui?.focusFirstInvalid(form);
        form.dispatchEvent(new CustomEvent('unionstack:form:error', { detail: { error } }));
    } finally {
        activeSubmissions.delete(form);
        setBusy(form, false);
    }
}

async function deleteResource(button) {
    const form = document.querySelector(button.dataset.usJsonDelete);
    if (!(form instanceof HTMLFormElement) || activeSubmissions.has(form)) {
        return;
    }

    activeSubmissions.add(form);
    const wasDisabled = button.disabled;
    button.disabled = true;
    setBusy(form, true);
    try {
        await requestJson(form.getAttribute('action'), { method: 'DELETE', headers: concurrencyHeaders(form) });
        const redirectUrl = button.dataset.usRedirectUrl;
        if (redirectUrl) {
            window.location.assign(resolveUrl(redirectUrl));
        }
    } catch (error) {
        const message = globalThis.UnionStackI18n.localMessage(error) || globalThis.UnionStackI18n.text('Common.MessageRequestFailed');
        setFormState(form, 'error', form.dataset.usErrorLabel || globalThis.UnionStackI18n.text('Common.Error'), message);
    } finally {
        activeSubmissions.delete(form);
        setBusy(form, false);
        button.disabled = wasDisabled;
    }
}

function enhanceJsonForms(root = document) {
    const forms = [...root.querySelectorAll('form[data-us-json-form]')];
    if (root.matches?.('form[data-us-json-form]')) forms.unshift(root);
    for (const form of forms) {
        if (form.dataset.usJsonFormEnhanced === 'true') {
            continue;
        }

        form.dataset.usJsonFormEnhanced = 'true';
        ensureFormFeedback(form);
        form.addEventListener('submit', event => {
            event.preventDefault();
            void submitForm(form);
        });
    }

    for (const button of root.querySelectorAll('[data-us-json-delete]')) {
        if (button.dataset.usJsonDeleteEnhanced === 'true') {
            continue;
        }

        button.dataset.usJsonDeleteEnhanced = 'true';
        button.addEventListener('click', () => void deleteResource(button));
    }

}

document.addEventListener('unionstack:datatable-form-shown', event => enhanceJsonForms(event.detail.form));

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceJsonForms(), { once: true });
} else {
    enhanceJsonForms();
}

export {
    enhanceJsonForms,
    hydrateJsonForm,
    refreshJsonForm,
    registerBeforeSubmit,
    requestJson,
    requestJsonResponse,
    resolveUrl,
    serializeJsonForm,
};
